import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { CallActions } from '../actions/call.actions';
import { Call } from '../models/call.model';
import { CallService } from 'src/app/services/call.service';
import { patch, append, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { app_date_time_format } from 'src/app/globals';
import {Photo} from "../models/photo.model";

export class CallStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    calls: Call[]
}

@State<CallStateModel>({
    name: 'call_entry',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        calls: []
    }
})
@Injectable()
export class CallState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private callEntryService: CallService,
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<CallStateModel>) {
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                 
                        await this.reloadStateFromStorage()   
                        this.store.dispatch(new CallActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {            
        if (this.storageService.is_initialized) {      
            const state_model: CallStateModel = change.currentValue
            this.storageService.set('calls', JSON.stringify(state_model))
        }      
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {            
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {                
                const state_model: CallStateModel = change.currentValue
                await this.offline.processPendingCalls(state_model.calls)
            } 
        }   
    }
    
    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('calls');
        if (json) {
            const state_model: CallStateModel = JSON.parse(json)
            if (state_model) {                
                this.store.dispatch(new CallActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new CallActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.callEntryService.getMine()
        if (objs != null) {
          const many = Call.buildMany(objs);
          const promises = [];
          for (const call of many) {
              promises.push(this.getAndSetPhotos(call));
          }
          await Promise.all(promises);

          this.store.dispatch(new CallActions.Merge(many))
          return true
        }
        return false
    }

    async getAndSetPhotos(call: Call) {
        const photos = await this.callEntryService.getPhotosByCall(call);
        if (photos) call.photos = await Photo.buildMany(photos);
    }
  
    @Selector()
    static getLastUpdate(state: CallStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: CallStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: CallStateModel) {
        return state
    }

    @Selector()
    static getAll(state: CallStateModel) {
        return state.calls
    }

    @Selector()
    static getOne(state: CallStateModel) {
        return (id: number) => {
            return state.calls.filter(s => s.id === id)
        }
    }

    @Action(CallActions.Refresh)
    async refresh({patchState}: StateContext<CallStateModel>, {  }:CallActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(CallActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<CallStateModel>, {  }:CallActions.IncrementPendingCount) {
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }
    
    @Action(CallActions.Init)
    init({setState, getState}: StateContext<CallStateModel>, { payload }:CallActions.Init) {
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new CallActions.Refresh())
    }
    
    @Action(CallActions.Set)
    set({setState, getState}: StateContext<CallStateModel>, { payload }:CallActions.Set) {
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing, 
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),         
            calls: payload
        })
    }

    @Action(CallActions.Merge)
    async merge({getState, setState}: StateContext<CallStateModel>, { payload }:CallActions.Merge) {
        // This is to merge data from the API provided in payload, into the app state.
        // Basically, if we have server data, we trust that, so we want to replace the 
        // current app state with the payload, except:
        // 1. Any objects pending upload or delete need to be merged in, even if it no longer exists.
        // 2. Same goes for any with a service_entry that is pending approval upload.
        const state = getState()
        const oldCalls = [...state.calls]
        let newCalls = [...payload]

        // For each wor in state now...
        for (const call_entry of oldCalls) {
            // If wor id is not in the list from the server
            if (!payload.find(f => f.id === call_entry.id)) {
                if (call_entry.deleted_photos.length > 0 || call_entry.photos.filter( f => f.uploaded === false).length > 0) {
                    newCalls.push(call_entry);
                }
            } else {
                let newCall = payload.find(f => f.id === call_entry.id);
                if (newCall) {
                    newCall.deleted_photos = call_entry.deleted_photos;
                    if (call_entry.photos && call_entry.photos.length > 0) {
                        for (const photo of call_entry.photos) {
                            if (!photo.uploaded) newCall.photos.push(await Photo.buildOne(photo));
                        }
                    }
                }
            }
        }

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),
            calls: newCalls
        })
    }

    @Action(CallActions.AddOne)
    addOne({getState, patchState}: StateContext<CallStateModel>, { payload }:CallActions.AddOne) {
        const state = getState()              
        patchState({
            calls: [...state.calls, payload]
        })
    }

    @Action(CallActions.UpdateOne)
    updateOne({setState}: StateContext<CallStateModel>, { payload }:CallActions.UpdateOne) {
        setState(
            patch<CallStateModel>
                ({
                    calls: updateItem((item: Call) => item.id === payload.id,
                        patch<Call>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(CallActions.DeleteOne)
    deleteOne({setState}: StateContext<CallStateModel>, { payload }:CallActions.DeleteOne) {
        setState(
            patch<CallStateModel>
                ({
                    calls: updateItem((item: Call) => item.id === payload.id,
                        patch<Call>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(CallActions.ApiAddedOne)
    apiAddedOne({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiAddedOne) {
        setState(
            patch<CallStateModel>
                ({
                    calls: updateItem((item: Call) => item.uuid === payload.uuid,
                        patch<Call>
                            ({
                                id: payload.id,
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(CallActions.ApiUpdatedOne)
    apiUpdatedOne({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiUpdatedOne) {
        setState(
            patch<CallStateModel>
                ({
                    calls: updateItem((item: Call) => item.id === payload.id,
                        patch<Call>
                            ({
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(CallActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiDeletedOne) {
        setState(
            patch<CallStateModel>
                ({
                    calls: removeItem((item: Call) => item.id === payload.id),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(CallActions.AddPhotoToCall)
    async addPhotoToCall({ setState }: StateContext<CallStateModel>, { payload }:CallActions.AddPhotoToCall) {
        setState(
            patch<CallStateModel>({
                calls: updateItem((item: Call) => item.id === payload.call_id, patch({photos: append([await Photo.buildOne(payload.photo)])}))
            })
        )
    }

    @Action(CallActions.DeletePhotoFromCall)
    deletePhotoFromCall({ setState }: StateContext<CallStateModel>, { payload }:CallActions.DeletePhotoFromCall) {
        setState(
            patch<CallStateModel>
            ({
                calls: updateItem((item: Call) => item.id === payload.call_id,
                    patch<Call>
                    ({
                        photos: removeItem(photo => photo.uuid === payload.photo.uuid),
                        deleted_photos: append([payload.photo])
                    })),
                last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")
            })
        )
    }

    @Action(CallActions.ApiAddedPhotoToCall)
    apiAddedPhotoToCall({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiAddedPhotoToCall) {
        setState(
            patch<CallStateModel>
            ({
                calls: updateItem((item: Call) => item.id === payload.call_id,
                    patch<Call>
                    ({
                        photos: updateItem((photo: Photo) => photo.uuid === payload.photo.uuid,
                            patch<Photo>({uploaded: true, id: payload.photo.id, file_data: payload.photo.file_data,
                                file_type: payload.photo.file_type, base64: payload.photo.base64,
                                base64_sz: payload.photo.base64_sz
                            })
                        )})),
                last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")
            })
        )
    }

    @Action(CallActions.ApiDeletedPhotoFromCall)
    apiDeletedPhotoFromCall({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiDeletedPhotoFromCall) {
        setState(
            patch<CallStateModel>({
                calls: updateItem((item: Call) => item.id === payload.call_id,
                    patch({photos: removeItem(photo => photo.uuid === payload.photo.uuid),
                        deleted_photos: removeItem(dphoto => dphoto.uuid === payload.photo.uuid)}))
            })
        )
    }

    @Action(CallActions.ApiStartUploading)
    apiStartUploading({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiStartUploading) {
        setState(
            patch<CallStateModel>
            ({
                calls: updateItem((item: Call) => item.id === payload.call_id,
                    patch<Call>
                    ({
                        photos: updateItem((photo: Photo) => photo.uuid === payload.photo.uuid,
                            patch<Photo>({uploading: true})
                        )})),
                last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")
            })
        )
    }

    @Action(CallActions.ApiStopUploading)
    apiStopUploading({ setState }: StateContext<CallStateModel>, { payload }:CallActions.ApiStopUploading) {
        setState(
            patch<CallStateModel>
            ({
                calls: updateItem((item: Call) => item.id === payload.call_id,
                    patch<Call>
                    ({
                        photos: updateItem((photo: Photo) => photo.uuid === payload.photo.uuid,
                            patch<Photo>({uploading: false})
                        )})),
                last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")
            })
        )
    }
}
